"use client";

import { motion, useReducedMotion } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";

/**
 * "Performance" on `/developers`, in the team's page-content document's own
 * words and figures (2026-10-21): P50 and P99 latency through the gateway,
 * sustained throughput on two vCPUs and the image size, then the four
 * gateways it was measured beside and the harness to rerun it. The home
 * page's gateway section quotes the same run as overhead added (1.99 ms);
 * this page quotes latency through the gateway (2.28 ms), as the document
 * does. Both come from `backend-docs/benchmarks/summary.md`.
 */

const BENCH_URL = "https://github.com/ENTERPILOT/ai-gateway-reproducible-benchmark";

const TILES = [
  { value: "2.28", unit: "ms", label: "P50 latency through the gateway" },
  { value: "6.75", unit: "ms", label: "P99 latency through the gateway" },
  { value: "4,520", unit: "req/s", label: "Sustained throughput on 2 vCPUs" },
  { value: "3.4", unit: "MB", label: "Docker image size" },
];

const RUNS = [
  { label: "P/9", value: 4520 },
  { label: "GoModel", value: 3961 },
  { label: "Bifrost", value: 2560 },
  { label: "Portkey", value: 993 },
  { label: "LiteLLM", value: 288 },
];
const MAX = 5000;

const LINK = "text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground";

/** Corner ticks on a square tile: the sharp frame the page's art uses. */
function Ticks() {
  const t = "absolute h-2.5 w-2.5 border-foreground/50";
  return (
    <>
      <span aria-hidden="true" className={cn(t, "left-0 top-0 border-l border-t")} />
      <span aria-hidden="true" className={cn(t, "right-0 top-0 border-r border-t")} />
      <span aria-hidden="true" className={cn(t, "bottom-0 left-0 border-b border-l")} />
      <span aria-hidden="true" className={cn(t, "bottom-0 right-0 border-b border-r")} />
    </>
  );
}

export function DevPerformance() {
  const reduce = useReducedMotion();
  return (
    <section id="performance" className="relative scroll-mt-24">
      <div className="mx-auto max-w-[90rem] px-5 py-14 sm:px-8 sm:py-24">
        <SectionHeading
          align="left"
          eyebrow="Performance"
          title="The gateway is a Go service on the request path: authenticate, check scope and budget, meter tokens, relay."
        />
        <p className="mt-5 max-w-2xl font-sans text-sm leading-relaxed text-muted">
          Its overhead is benchmarked with an open, reproducible harness against a deterministic mock
          upstream.
        </p>

        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-12 grid grid-cols-2 gap-3 lg:grid-cols-4"
        >
          {TILES.map((t) => (
            <motion.div key={t.label} variants={fadeUp} className="relative border border-border bg-card/40 p-5 sm:p-6">
              <Ticks />
              <p className="font-serif text-[clamp(1.8rem,3.4vw,2.6rem)] font-light leading-none tracking-tight text-foreground">
                {t.value}
                <span className="ml-1.5 font-sans text-sm text-muted">{t.unit}</span>
              </p>
              <p className="mt-3 font-sans text-xs leading-snug text-muted">{t.label}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] lg:items-end">
          <div className="relative border border-border p-5 sm:p-6">
            <Ticks />
            <p className="font-sans text-xs font-medium text-foreground">
              Sustained requests per second, one instance of each gateway (higher is better)
            </p>
            {/* The list, not each bar, watches the viewport: a bar starts at
                zero width, and a zero-area element never counts as in view. */}
            <motion.ul
              className="mt-4 flex flex-col gap-3"
              initial={reduce ? false : "hidden"}
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
            >
              {RUNS.map((r, i) => (
                <li key={r.label} className="grid grid-cols-[4.5rem_1fr_5rem] items-center gap-3">
                  <span className={cn("font-mono text-xs", i === 0 ? "text-foreground" : "text-muted")}>{r.label}</span>
                  <span className="relative h-2.5 bg-foreground/[0.06]">
                    <motion.span
                      className={cn("absolute inset-y-0 left-0", i === 0 ? "bg-foreground" : "bg-foreground/35")}
                      variants={{
                        hidden: { width: 0 },
                        show: {
                          width: `${(r.value / MAX) * 100}%`,
                          transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.08 },
                        },
                      }}
                      style={reduce ? { width: `${(r.value / MAX) * 100}%` } : undefined}
                    />
                  </span>
                  <span className="text-right font-sans text-xs tabular-nums text-muted">
                    {r.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} req/s
                  </span>
                </li>
              ))}
            </motion.ul>
          </div>
          <div className="flex flex-col gap-3 font-sans text-sm leading-relaxed text-muted">
            <p>
              Benchmarked against LiteLLM (288 req/s), Portkey (993 req/s), Bifrost (2,560 req/s), and
              GoModel (3,961 req/s) on the same host and mock upstream. Harness is open source.
            </p>
            <p>
              Benchmark repo:{" "}
              <a href={BENCH_URL} target="_blank" rel="noopener noreferrer" className={cn(LINK, "break-all")}>
                github.com/ENTERPILOT/ai-gateway-reproducible-benchmark
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
